import {Component, Inject, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, NgForm, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/services/auth.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Observable, of, switchMap, take} from 'rxjs';
import {AACDataService} from '../../../services/aac-data.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserService} from "../../../services/user.service";
import {AACUser} from "../../../models/AAC_User.model";
import {TrialDialogComponent} from "../sign-in/sign-in.component";

export interface DialogData {
    text: string;
    name: string;
    businessUnit: string;
    businessUnitName: string;
    serviceLevel: string;
}

@Component({
    selector     : 'auth-sign-up',
    templateUrl  : './sign-up.component.html',
    encapsulation: ViewEncapsulation.None,
    animations   : fuseAnimations
})
export class AuthSignUpComponent implements OnInit
{
    @ViewChild('signUpNgForm') signUpNgForm: NgForm;

    alert: { type: FuseAlertType; message: string } = {
        type   : 'success',
        message: ''
    };
    signUpForm: UntypedFormGroup;
    showAlert = false;
    registrationInformation: any = {};
    isLoading= false;
    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _userService: UserService,
        private _aacData: AACDataService,
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        private _snackBar: MatSnackBar,
        public dialog: MatDialog
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.registrationInformation.agentFirstName = this._authService.tokenData.given_name;
        this.registrationInformation.agentId = this._authService.tokenData.icAgentId;
        this.registrationInformation.agentLastName = this._authService.tokenData.family_name;
        this.registrationInformation.businessUnitName = this._authService.businessUnit.businessUnitName;
        this.registrationInformation.businessUnit = this._authService.tokenData.icBUId;
        this.registrationInformation.clusterId = this._authService.tokenData.icClusterId;
        this.registrationInformation.businessUnitType = 'UserHub';
        this.registrationInformation.businessUnitDomain = 'Domain';
        this.registrationInformation.username = this._authService.tokenData.name;
        this._userService.user$
            .pipe(take(1))
            .subscribe((user: AACUser) => {
                if (user) {
                    this.registrationInformation.emailAddress = user.cxOneAgent.emailAddress;
                }
            });
        // Create the form
        this.signUpForm = this._formBuilder.group({
                firstName      : [this.registrationInformation.agentFirstName, Validators.required],
                lastName      : [this.registrationInformation.agentLastName, Validators.required],
                email     : [this.registrationInformation.emailAddress, [Validators.required, Validators.email]],
                company   : [this._authService.businessUnit.businessUnitName],
                emailOptIn : [''],
                acknowledge : [false, Validators.requiredTrue]
            }
        );
        this.signUpForm.get('email').disable({emitEvent: false});
        this.signUpForm.get('company').disable({emitEvent: false});
        this.redirectAuthUsers();

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    submitRegistration(): void {
        this.isLoading = true;
        this._authService.checkUser().pipe(switchMap((loginResult) => {
            if (loginResult.message === 'Unauthenticated') {
                return of({success:false, message:'User Not Authenticated'});
            } else if (loginResult.message === 'BU Not Registered') {
                return this.registerBusinessUnit().pipe(switchMap((buResult) => {
                        if(buResult.success) {
                            return this.registerUser();
                        }
                    }
                ));
            }
            else{
                return this.registerUser();
            }
        })).subscribe( (regResult)=>{
            if (regResult.success)
            {
                this.redirectAuthUsers();
            }
        });
    }

    redirectAuthUsers(): void{
        this._authService.checkUser().subscribe((checkResult)=>{
            if (checkResult.isAuthenticated){
                this.openTrialTermsDialog();
                this._router.navigateByUrl('signed-in-redirect').then();
            }
           });
    }

    registerBusinessUnit(): Observable<any>{
        return this._aacData.registerBusinessUnit(
            this.registrationInformation.businessUnit,
            this.registrationInformation.businessUnitName,
            'UserHub',
            this._authService.apiHelper.domainPrefix,
            this.registrationInformation.clusterId,
            this.registrationInformation.username).pipe(switchMap((response) =>
            {
                if (response.success && response.aacBusinessUnit && response.aacBusinessUnit.id){
                    this.registrationInformation.businessUnitId = response.aacBusinessUnit.id;
                    return of({success: true, message: 'Registered Business Unit'});
                }
                    // this._authService.clearLoginInfo();
                this.displayMessage('Error registering business unit with Skybox');
                return of({success: false, message: 'Error registering business unit with Skybox'});
            }));
    }

    registerUser(): Observable<any>
    {
        return this._aacData.registerUser(
            this.registrationInformation.agentId,
            this.signUpForm.value.firstName,
            this.signUpForm.value.lastName,
            this.registrationInformation.username,
            this.signUpForm.getRawValue().email,
            this.registrationInformation.businessUnitId,
            'Y',
            'Y',
            'Y',
            this.registrationInformation.businessUnit
        ).pipe(switchMap((response) =>
        {
            if (response.success && response.aacUser && response.aacUser.id){
                // set session and log in
                this.registrationInformation.aacUserId = response.aacUser.id;
                if (response.aacUser.isApproved === 'Y'){
                    this.isLoading = false;
                    return of({success: true, message: 'User Created - Approved'});
                }
                else{
                    this.isLoading = false;
                    //this.displayInfoMessage('Your registration request has been successfully sent to Skybox Technologies and is awaiting approval');
                    return of({success: true, message: 'User Created - Awaiting Approval'});
                }
            }
            else{
                this.displayMessage('Error registering user with Skybox:  ' + response.message);
            }
            return of({success: false, message: 'User Not Authenticated'});
        }));
    }


    public displayMessage(message: string): void {
        this.isLoading = false;
        this._snackBar.open(message, 'OK', {
            verticalPosition: 'top',
            duration        : 5000,
            panelClass: ['mat-toolbar', 'mat-warn']
        });
    }

    public displayInfoMessage(message: string): void {
        this.isLoading = false;
        this._snackBar.open(message, 'OK', {
            verticalPosition: 'top',
            duration        : 5000,
            panelClass: ['mat-toolbar', 'mat-accent']
        });
    }

    openTermsDialog(): void {
        let terms = 'By registering you understand that you will be dealing with live Business Unit data.  ';
        terms += 'Skybox Technologies is not responsible for any issues to the Business Unit due to data changes made from this application.';
        const dialogRef = this.dialog.open(TermsDialogComponent, {
            width: '550px',
            data: {text: terms}
        });

        dialogRef.afterClosed().subscribe((result) => {

        });

    }

    openHelpDialog(helptype: string): void {

        let helpTitle: string;
        let helpText: string;
        let helpText2: string;
        let imageName: string;
        let imageName2: string;

        switch (helptype) {
            case 'butype':
                helpTitle = 'Business Unit Type';
                helpText = 'You can determine your Business Unit type by logging in to your Contact Center and navigating to the Admin section.';
                helpText += '  If you see a domain on the top of NA1, NA2, CA1, AU, or EU followed by .nice-incontact.com you are on a CXone Contact Center.';
                helpText += '  Central users will see home-{your cluster} followed by .incontact.com';
                imageName = 'userhub.png';
                helpText2 = '';
                imageName2 = '';
                break;
            case 'fedramp':
                helpTitle = 'FedRAMP';
                helpText = 'A FedRAMP cluster is used in instances of high security such as with the military or government offices.';
                helpText += '  You can determine your Business Unit is FedRAMP by looking for the cluster of NA2 and c72 on CXone or c71 in Central.';
                imageName = '';
                helpText2 = '';
                imageName2 = '';
                break;
            case 'logintype':
                helpTitle = 'Login Type';
                helpText = 'AAC supports the ability to log in by using your Contact Center credentials as well as a secure Access Key set up in the Contact Center.';
                helpText += '  In the case of SSO where you do not have a username and password, an Access Key can be used.<br>';
                helpText += '  In CXone, you can add new Access Key in the Admin->Employees section.';
                imageName = 'userhub_access_key.png';
                helpText2 = 'In Central, you can add an Access Key in the Admin->Users section.';
                imageName2 = 'central_access_key.png';
                break;
            case 'createapplication':
                helpTitle = 'Create API Application';
                helpText = 'Central users will need to create a new API Application in their Contact Center.';
                helpText += '  The application should have access to all APIs in the system.  For more information on how to create API Applications,';
                helpText += ' Click <a href=\'https://help.incontact.com/Spring20/EN/Content/ACD/APIApplications/CreateAnAPIApplication.htm?Highlight=create%20application\' target=\'_blank\'>here</a>';
                imageName = '';
                helpText2 = '';
                imageName2 = '';
                break;
        }

        if (helpText){
            let helpData = '{"title":"' + helpTitle + '"';
            helpData += ',"text":"' + helpText + '"';
            helpData += ',"text2":"' + helpText2 + '"';
            helpData += ',"imageName":"' + imageName + '"';
            helpData += ',"imageName2":"' + imageName2 + '"}';
            const dialogRef = this.dialog.open(HelpDialogComponent, {
                width: '550px',
                data: {text: helpData}
            });

            dialogRef.afterClosed().subscribe();
        }
    }
    getPricing(): void{
        window.open('https://skyboxcommunications.com/skybox-cxone-advanced-administrator/', '_blank');
    }
    openTrialTermsDialog(): void {
        let terms = 'The AAC registration for Business Unit ' + this.registrationInformation.businessUnit + ' (' + this.registrationInformation.businessUnitName + ') is set up for a trial period of 90 days.';
            terms += '  There are 90 days remaining in this trial.';
            terms += '  At the end of the trial period, you will no longer have access to this account.  Please contact Skybox Technologies at any time to ';

        this.dialog.open(TrialDialogComponent, {
            width: '550px',
            disableClose: true,
            data: {text: terms, businessUnitName: this.registrationInformation.businessUnitName, businessUnit: this.registrationInformation.businessUnit, name: this.signUpForm.value.firstName + ' ' + this.signUpForm.value.lastName, serviceLevel: 'TRIAL'}
        });
    }
}


@Component({
    selector: 'terms-dialog',
    templateUrl: 'terms-dialog.html',
})
export class TermsDialogComponent {

    constructor(
        public dialogRef: MatDialogRef<TermsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData) {}

    onNoClick(): void {
        this.dialogRef.close();
    }
}

@Component({
    selector: 'help-dialog',
    templateUrl: 'help-dialog.html',
})
export class HelpDialogComponent implements OnInit {

    title: string;
    text: string;
    imageName: string;
    imageName2: string;
    imageSource: string;
    imageSource2: string;
    text2: string;

    constructor(
        public dialogRef: MatDialogRef<HelpDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData) {}

    ngOnInit(): void {
        const jsonData = JSON.parse(this.data.text);
        this.title = jsonData.title;
        this.text = jsonData.text;
        this.text2 = jsonData.text2;
        this.imageName = jsonData.imageName;
        this.imageName2 = jsonData.imageName2;
        this.imageSource = './assets/images/help/' + this.imageName;
        this.imageSource2 = './assets/images/help/' + this.imageName2;
    }

    onNoClick(): void {
        this.dialogRef.close();
    }
}
